import { Fragment, memo, useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { FilterList, VisibilityOutlined } from "@mui/icons-material"
import { Chip, Tooltip } from "@mui/material"
import ListPagination from "../../common/ListPagination/ListPagination"
import moment from "moment"
import logger from "../../../Logger/index.js"
import { ctfEnabled, isTrainer, listIncludesUserEmail, userOrg } from "../../../data-utils/authUtils.js"
import { useKpiEntitlement } from "../../../data-utils/kpiHooks"
import { KPI_VIEW, kpiViewFor } from "../../../data-utils/kpiView"
import { groupDashboardURL } from "../../../data-utils/gameSessionGroupUtils.js"
import {
  duplicateGameURL,
  editGameURL,
  invitableParticipants,
  playboardURL,
  reportURL,
  ctfReportURL,
  ctfEventReportURL,
  restartGameURL,
} from "../../../data-utils/gameSessionUtils.js"
import { useDebounce } from "../../../hooks/useDebounce"
import useDeepCompareEffect from "../../../hooks/useDeepCompareEffect.js"
import useLocalStorage from "../../../hooks/useLocalStorage"
import { GamelistActions } from "../../../store/gamelist-slice"
import {
  ROUTE_PATHS,
  TABLE_GAME_ADMIN_ACTION,
  TABLE_GAME_FILTERS,
  TABLE_GAME_HISTORY_ACTION,
  TABLE_GAME_SCHEDULE_ACTION,
  TABLE_GAME_STATUS_FILTER,
  TABLE_GAME_USER_ACTION,
  TABLE_SORT_ORDER,
} from "../../../utilities/constants"
import HelpTip from "../../common/Help/HelpTip"
import { getIconRender } from "../../common/Icons/icon-utils.jsx"
import Loader from "../../common/Loader/loader.jsx"
import { UiCtButton } from "../../common/MuiStyled/LibComponent.jsx"
import CustomFilter from "../../common/custom-filter/custom-filter"
import "./Home.scss"
import GameAction from "./ListCellComponents/GameAction.jsx"
import UserProfiles from "./ListCellComponents/UserProfiles.jsx"
import { dynamicQuestionsLine, dynamicQuestionsOf, dynamicStatusTone } from "../../../data-utils/ctfDynamicQuestions"
import EmptyGame from "./emptyGame.jsx"
import MobileFilterDrawer from "./MobileFilterDrawer/MobileFilterDrawer.jsx"
import { matchesSetupFilter } from "./exerciseLinkFilters"
import ExerciseTypeBadge from "../../common/ExerciseType/ExerciseTypeBadge"
import ExerciseTypeFilter from "../../common/ExerciseType/ExerciseTypeFilter"
import { exerciseTypeOf, matchesTypeFilter, typeCounts } from "../../../data-utils/exerciseType"
import { inviteFailuresTooltip } from "../../../data-utils/inviteFailures.js"

// Letters, marks and numbers in any script are kept (东京 and 大阪 stay different); only separators fold.
const normName = (t) =>
  String(t ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\p{N}]+/gu, " ")
    .trim()
// A scenario line reading "Acme — Holiday-Peak Ransomware" under an exercise of the same name (or that
// name plus " - Oct 13") says nothing new. Whole words only: "Ransomware" doesn't swallow "Ransomware2".
export const startsWithName = (exerciseName, scenarioName) => {
  const scenario = normName(scenarioName)
  const exercise = normName(exerciseName)
  return Boolean(scenario) && (exercise === scenario || exercise.startsWith(`${scenario} `))
}

const pageLogger = logger.child({ module: "Exercise list" })

const LIST_PAGE_SIZE = 20

/**
 * Memoized table row component to prevent unnecessary re-renders
 */
const GameTableRow = memo(function GameTableRow({
  gameData,
  dataIndex,
  tableColumnHeaders,
  managerView,
  getTableCellData,
  isGroupOpened,
  getGroupContent,
  sponsorView,
}) {
  return (
    <Fragment key={`table-data-row-${dataIndex}`}>
      <tr
        className={`table-row align-middle ${
          gameData.isGroupRoot ? "group-root" : gameData.isGroupNode ? "group-node" : ""
        } ${gameData.archived && managerView ? "archived" : ""}`}
      >
        {tableColumnHeaders.map((headerObj, headerIndex) => (
          <td key={`table-data-row-${dataIndex}-column-${headerIndex}`} className="table-cell">
            {getTableCellData(headerObj.headerKey, gameData)}
          </td>
        ))}
      </tr>
      <tr
        className={`row-spacer ${
          gameData.isGroupRoot || gameData.isGroupNode ? (isGroupOpened(gameData.groupId) ? "group-spacer-opened" : "group-spacer") : ""
        }`}
      >
        <td className="table-cell" colSpan={tableColumnHeaders.length}></td>
      </tr>
      {gameData.groupId && isGroupOpened(gameData.groupId)
        ? getGroupContent({
            groupId: gameData.groupId,
            isManagerView: managerView,
            isSponsorView: sponsorView,
          })
        : null}
    </Fragment>
  )
})

GameTableRow.displayName = "GameTableRow"

function GameList({
  gameListData,
  searchText: st,
  filters,
  filterData,
  filterSearchProgress = false,
  userData,
  sponsorView,
  managerView,
  showOrgColumn = false,
  orgData,
  authUser,
  handleSendInvite,
  handleDownloadCtfPack = null,
  handleArchiveGame,
  handleUnarchiveGame,
  handleMarkGameFinished,
  handleUnmarkGameFinished,
  handleArchiveGroupGame,
  handleUnarchiveGroupGame,
  handleMarkGroupGameFinished,
  handleUnmarkGroupGameFinished,
  handleAddUsers,
  handleDeleteGame,
  setupFilter = null,
  typeFilter = null,
  onTypeFilterChange = null,
  filterChips = null,
}) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  // KPI P2 §10: the Score column and "Compare runs" to the Compare tab follow each EXERCISE's org
  // (a partner's or platform admin's list mixes orgs): org_kpi from the list, else (an older server)
  // the viewer's org. A partner/platform admin also gets Compare runs on an exercise whose org has no
  // KPI analytics (the tab explains why it's empty).
  const { enabled: kpiOrgEnabled } = useKpiEntitlement()
  const staffView = !!(sponsorView || managerView || isTrainer(authUser))
  const viewerOrgId = userOrg(authUser) || null
  const kpiOfRow = (row) => (typeof row?.org_kpi === "boolean" ? row.org_kpi : kpiOrgEnabled)
  const kpiViewOfRow = (row) => {
    const crossOrg = !!viewerOrgId && !!row?.org_id && row.org_id !== viewerOrgId
    const trainer = isTrainer(authUser)
    return kpiViewFor({ manager: !!managerView, sponsor: !!sponsorView && !trainer, trainer, crossOrg, kpi: kpiOfRow(row) })
  }
  // Why a row shows no score: the org has KPI analytics off, or it's on but outside what you can see.
  const kpiMissingReason = (row) => {
    const name = row?.org_name || "this organization"
    return kpiOfRow(row) ? `KPI results for ${name} aren't available to you` : `KPI analytics isn't turned on for ${name}`
  }
  const kpiColumns = staffView && (kpiOrgEnabled || (gameListData ?? []).some((row) => kpiViewOfRow(row) === KPI_VIEW.DATA))
  // Sort Mode can be one of 'none' | 'asc' | 'desc'
  const [sortMode, setSortMode] = useState({
    org_name: filterData[TABLE_GAME_FILTERS.ORGANIZATION] ?? TABLE_SORT_ORDER.NONE,
    game_schedule: filterData[TABLE_GAME_FILTERS.TIME_SORT] ?? TABLE_SORT_ORDER.DESC,
  })

  // Grouping Table Data
  const [groupOpenStatus, setGroupOpenStatus] = useState(null)
  const [groupNodes, setGroupNodes] = useState(null)

  // Mobile filter drawer state
  const [mobileFilterDrawerOpen, setMobileFilterDrawerOpen] = useState(false)
  const [listPage, setListPage] = useState(1)
  // Persist the user's rows-per-page choice so it sticks across visits (CT: exercises list)
  const [listPageSize, setListPageSize] = useLocalStorage("ct-exercises-page-size", LIST_PAGE_SIZE)

  pageLogger.info({ groupOpenStatus }, `groupOpenStatus`)
  pageLogger.info({ groupNodes }, `groupNodes`)

  const groupTableHeaderKey = { headerKey: "group_key", headerLabel: "", minWidth: 40 }

  const initialTableData = {
    tableData: [],
    groupedData: [],
    tableColumnHeaders: [
      { headerKey: "game_name", headerLabel: "Exercise", minWidth: 170, headerTooltip: "Exercise name and the scenario it runs" },
      { headerKey: "game_schedule", headerLabel: "Start Time", minWidth: 96, headerTooltip: "When the exercise started (or is scheduled to)" },
      {
        headerKey: "people",
        headerLabel: "People",
        minWidth: 96,
        headerTooltip: "Players taking part, then view-only observers (eye icon)",
      },
      { headerKey: "status", headerLabel: "Status", minWidth: 76, headerTooltip: "Scheduled, ongoing, or finished" },
      { headerKey: "age", headerLabel: "Age", minWidth: 54, headerTooltip: "Time since the exercise started" },
      { headerKey: "user_action", headerLabel: "", minWidth: 108 },
      { headerKey: "action", headerLabel: "", minWidth: 20 },
    ],
  }

  if (managerView || showOrgColumn) {
    // Insert the org column for managers and partners (canManageChildOrgs)
    initialTableData.tableColumnHeaders.splice(0, 0, {
      headerKey: "org_name",
      headerLabel: "Organization",
      minWidth: 110,
    })
  }

  const sortableColumnKeys = [/* "org_name", */ "game_schedule"] // TODO

  const tableDataRef = useRef(initialTableData)
  const withKpiColumns = (headers) => {
    if (!kpiColumns) return headers
    const at = headers.findIndex((h) => h.headerKey === "status")
    const extra = [
      {
        headerKey: "kpi_score",
        headerLabel: "Score",
        minWidth: 56,
        headerTooltip: "Finished assessment score (a group shows the mean of its members)",
      },
    ]
    return at < 0 ? [...headers, ...extra] : [...headers.slice(0, at), ...extra, ...headers.slice(at)]
  }
  const visibleRows = (rows) =>
    rows.filter((r) => matchesSetupFilter({ row: r, setup: setupFilter }) && matchesTypeFilter({ row: r, type: typeFilter }))

  const getLastIdAfterComma = (groupId) => {
    const ids = String(groupId)
      .split(",")
      .map((ele) => ele.trim())
    return ids[ids.length - 1]
  }

  const updateGroupTableData = useCallback(
    (tableData) => {
      const _openStatus = {}
      Object.assign(_openStatus, groupOpenStatus)
      const _groupNodes = {}
      tableData.forEach((data) => {
        if (data.groupId) {
          const groupIds = String(data.groupId)
            .split(",")
            .map((ele) => ele.trim())
          groupIds.forEach((id) => {
            _openStatus[id] = groupOpenStatus?.[id] ?? false
            _groupNodes[id] = tableData.filter((filterEle) => {
              if (filterEle.groupId && filterEle.groupId.includes(id)) {
                if (
                  (filterEle.groupId.includes(",") && id !== getLastIdAfterComma(filterEle.groupId)) ||
                  (!filterEle.groupId.includes(",") && filterEle.isGroupNode && id === getLastIdAfterComma(filterEle.groupId))
                ) {
                  return true
                }
                return false
              }
              return false
            })
          })
        }
      })
      setGroupOpenStatus(_openStatus)
      setGroupNodes(_groupNodes)
    },
    [groupOpenStatus]
  )

  const isGroupOpened = useCallback(
    (groupId) => {
      const selectedGroupId = getLastIdAfterComma(groupId)
      return groupOpenStatus?.[selectedGroupId] || false
    },
    [groupOpenStatus]
  )

  const toggleGroupContent = (groupId) => {
    const selectedGroupId = getLastIdAfterComma(groupId)
    setGroupOpenStatus((prevStatus) => ({
      ...prevStatus,
      [selectedGroupId]: !prevStatus[selectedGroupId],
    }))
  }

  useDeepCompareEffect(() => {
    if (gameListData && Array.isArray(gameListData)) {
      tableDataRef.current.tableData = gameListData
      tableDataRef.current.groupedData = gameListData.filter((ele) => !ele.isGroupNode)
      if (tableDataRef.current.groupedData.length > 0 && !tableDataRef.current.tableColumnHeaders.find((ele) => ele.headerKey === "group_key")) {
        tableDataRef.current.tableColumnHeaders = [groupTableHeaderKey, ...tableDataRef.current.tableColumnHeaders]
      }
      updateGroupTableData(gameListData)
    }
  }, [gameListData])

  const [searchText, setSearchText] = useState(st) // Search Text
  const searchQueryText = useDebounce(searchText, 1000)

  useEffect(() => {
    dispatch(GamelistActions.setSearchText(searchQueryText))
  }, [dispatch, searchQueryText])

  // Back to page 1 whenever the visible set changes shape (search/filter/sort, and the client-side
  // setup filter)
  useEffect(() => {
    setListPage(1)
  }, [searchQueryText, filterData, sortMode, setupFilter])

  const handleKeyEventsOnSearch = (event) => {
    if (event.key === "Enter" || event.keyCode === 13) {
      pageLogger.info(`To be searched value is : "${event.target.value}"`)
      setSearchText(event.target.value)
    }
  }

  const handleSelectionChange = useCallback(
    (filterKey, selectedOption) => {
      const filterData = {
        filterParam: filterKey,
        filterValue: selectedOption.optionValue,
      }
      dispatch(GamelistActions.setGamelistFilters(filterData))
    },
    [dispatch]
  )

  const handleTableSortClick = useCallback(
    (tableKey) => {
      const updatedSortMode = { ...sortMode }
      updatedSortMode[tableKey] =
        updatedSortMode[tableKey] === TABLE_SORT_ORDER.NONE
          ? TABLE_SORT_ORDER.ASC
          : updatedSortMode[tableKey] === TABLE_SORT_ORDER.ASC
            ? TABLE_SORT_ORDER.DESC
            : TABLE_SORT_ORDER.NONE

      if (tableKey === "game_schedule") {
        const filterData = {
          filterParam: TABLE_GAME_FILTERS.TIME_SORT,
          filterValue: updatedSortMode[tableKey],
        }
        dispatch(GamelistActions.setGamelistFilters(filterData))
      }

      if (tableKey === "org_name") {
        const filterData = {
          filterParam: TABLE_GAME_FILTERS.ORGANIZATION,
          filterValue: updatedSortMode[tableKey],
        }
        dispatch(GamelistActions.setGamelistFilters(filterData))
      }

      setSortMode(updatedSortMode)
    },
    [sortMode, dispatch]
  )

  const handleUserActionInviteClick = useCallback(
    ({ game, email }) => {
      handleSendInvite({ game, email })
    },
    [handleSendInvite]
  )

  const handleArchiveGameClick = useCallback(
    ({ game }) => {
      handleArchiveGame({ game })
    },
    [handleArchiveGame]
  )

  const handleArchiveGroupGameClick = useCallback(
    ({ game, org }) => {
      handleArchiveGroupGame({ game, org })
    },
    [handleArchiveGroupGame]
  )

  const handleUnarchiveGameClick = useCallback(
    ({ game }) => {
      handleUnarchiveGame({ game })
    },
    [handleUnarchiveGame]
  )

  const handleUnarchiveGroupGameClick = useCallback(
    ({ game, org }) => {
      handleUnarchiveGroupGame({ game, org })
    },
    [handleUnarchiveGroupGame]
  )

  const handleMarkGameFinishedClick = useCallback(
    ({ game }) => {
      handleMarkGameFinished({ game })
    },
    [handleMarkGameFinished]
  )

  const handleUnmarkGameFinishedClick = useCallback(
    ({ game }) => {
      handleUnmarkGameFinished({ game })
    },
    [handleUnmarkGameFinished]
  )

  const handleMarkGroupGameFinishedClick = useCallback(
    ({ game, org }) => {
      handleMarkGroupGameFinished({ game, org })
    },
    [handleMarkGroupGameFinished]
  )

  const handleUnmarkGroupGameFinishedClick = useCallback(
    ({ game, org }) => {
      handleUnmarkGroupGameFinished({ game, org })
    },
    [handleUnmarkGroupGameFinished]
  )

  const handleAddUsersClick = useCallback(
    ({ game, org, isGroupRoot }) => {
      handleAddUsers?.({ target: game, org, isGroupRoot })
    },
    [handleAddUsers]
  )

  const handleClearSearchInputClick = useCallback(() => {
    // Dispatch new query w/o debounce
    setSearchText("")
    dispatch(GamelistActions.setSearchText(""))
  }, [dispatch])

  const handleMobileFilterOpen = useCallback(() => {
    setMobileFilterDrawerOpen(true)
  }, [])

  const handleMobileFilterClose = useCallback(() => {
    setMobileFilterDrawerOpen(false)
  }, [])

  // Get Group-key Symbol
  const getGroupKeySymbol = (keyName, gameData) => {
    return gameData.isGroupRoot ? (
      <span className={`action-icon d-flex justify-content-center`} onClick={(_) => toggleGroupContent(gameData.groupId)}>
        {gameData.groupId && isGroupOpened(gameData.groupId) ? (
          <span className="icon-wrapper">
            <i className={`fa-solid fa-angle-down`}></i>
          </span>
        ) : (
          <span className="icon-wrapper">
            <i className={`fa-solid fa-chevron-right`}></i>
          </span>
        )}
      </span>
    ) : null
  }

  // Get Game Scenario Cell Data
  const getScenarioName = (keyName, gameData) => {
    return (
      <div className="scenario-wrapper">
        <span className="icon-wrapper me-2">
          {["fish", "anchor"].includes(gameData[keyName].icon_name[0]) ? (
            <span className="material-icons">phishing</span>
          ) : (
            getIconRender({
              iconName: gameData[keyName].icon_name[0],
              iconKeyPrefix: "game-scenario-icon",
              iconIndex: 0,
            })
          )}
        </span>
        <span>{`${gameData[keyName].scenario_name}`}</span>
      </div>
    )
  }

  // Get Game Name Cell Data — two-line primary cell: exercise name (bold) with the
  // scenario name muted beneath (the scenario column was merged in here). No scenario
  // icon in rows per the redesign; getScenarioName is kept for any other consumers.
  const getGameName = (keyName, gameData) => {
    const scenarioName = gameData?.game_scenario?.scenario_name
    // Dynamic CTF only (staff rows carry the summary; tabletop rows never do): this exercise's generated
    // question set — "Questions: Generating…" / "Questions: Ready · 12 questions".
    const dynamicQs = dynamicQuestionsOf(gameData)
    const dynamicLine = dynamicQs ? dynamicQuestionsLine(dynamicQs) : null
    // Show only what adds information: the type chip only for the exception types (Training,
    // CTF, mixed — most rows are Assessments) and never while the list is filtered to one type;
    // the scenario name only when the exercise name doesn't already start with it.
    const showTypeChip = !gameData?.isGroupNode && !typeFilter && (gameData?.exerciseTypeResolved ?? exerciseTypeOf(gameData)) !== "assessment"
    const showScenario = Boolean(scenarioName) && !startsWithName(gameData?.game_id, scenarioName)
    return (
      <div className="game-name-cell">
        <span className="game-name-text">{gameData?.game_id ? `${gameData.game_id}` : ""}</span>
        {showTypeChip || showScenario ? (
          <span className="game-scenario-sub">
            {showTypeChip ? <ExerciseTypeBadge row={gameData} className="me-1" /> : null}
            {showScenario ? scenarioName : null}
          </span>
        ) : null}
        {dynamicLine ? (
          <span className={`game-scenario-sub ct-dyn-line is-${dynamicStatusTone(dynamicQs.status)}`} data-testid="dynamic-questions-line">
            {dynamicLine}
          </span>
        ) : null}
      </div>
    )
  }

  // Get Organization Data
  const getOrganizationName = (keyName, gameData) => {
    return gameData[keyName] ? <span>{gameData[keyName]}</span> : null
  }

  // Get Game Schedule Cell — compact mono timestamp (reference-table idiom)
  const getGameSchedule = (keyName, gameData) => {
    const epochMs = gameData[keyName]?.epochMs
    return (
      <div className="schedule-wrapper">
        <span>{epochMs ? moment(epochMs).format("MMM D, YYYY HH:mm") : `${gameData[keyName].start_date}, ${gameData[keyName].start_time}`}</span>
      </div>
    )
  }

  // Relative age since start (display-only; derives from the same epoch)
  const getGameAge = (gameData) => {
    const epochMs = gameData?.game_schedule?.epochMs
    return epochMs ? <span className="age-cell">{moment(epochMs).fromNow()}</span> : <span className="age-cell">—</span>
  }

  const getGameStatus = (gameStatus, gameData = {}) => {
    // A scheduled exercise whose dispatch failed carries a sanitized `dispatchError`
    // marker. Status stays SCHEDULED (server keeps the enum unchanged); we surface the
    // failure as a distinct red "Failed to start" chip in place of the Scheduled chip.
    // The marker auto-clears on the next successful dispatch.
    const dispatchError = gameData?.dispatchError
    if (gameStatus === TABLE_GAME_STATUS_FILTER.SCHEDULED && dispatchError) {
      return (
        <div className="game-status-wrapper" data-game-status={`${gameStatus}`} data-dispatch-error="true">
          <Tooltip title={dispatchError.message || "This exercise couldn't be started"} arrow>
            <Chip color="error" size="small" icon={<i className="fa-solid fa-triangle-exclamation" />} label="Failed to start" />
          </Tooltip>
        </div>
      )
    }

    const uiDataObj = { iconName: "", actionText: "", navigationLink: "" }
    if (gameStatus === TABLE_GAME_STATUS_FILTER.ONGOING) {
      uiDataObj.iconName = "repeat"
      uiDataObj.actionText = TABLE_GAME_STATUS_FILTER.ONGOING
    } else if (gameStatus === TABLE_GAME_STATUS_FILTER.SCHEDULED) {
      uiDataObj.iconName = "clock"
      uiDataObj.actionText = TABLE_GAME_STATUS_FILTER.SCHEDULED
    } else if (gameStatus === TABLE_GAME_STATUS_FILTER.FINISHED) {
      uiDataObj.iconName = "circle-check"
      uiDataObj.actionText = TABLE_GAME_STATUS_FILTER.FINISHED
    }
    // Started without some participants: their invite email was undeliverable (the email provider
    // marked the address inactive, or it is invalid). Everyone else was invited; flag who wasn't.
    const inviteFailures = Array.isArray(gameData?.inviteFailures) ? gameData.inviteFailures.filter((f) => f?.email) : []
    return (
      <div className="game-status-wrapper" data-game-status={`${gameStatus}`}>
        <span className="status-dot" aria-hidden="true"></span>
        <span className="status-text">{uiDataObj.actionText}</span>
        {inviteFailures.length > 0 && (
          <Tooltip title={inviteFailuresTooltip(inviteFailures)} arrow>
            <span className="status-invite-warning" data-testid="invite-failures" role="img" aria-label={inviteFailuresTooltip(inviteFailures)}>
              <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
            </span>
          </Tooltip>
        )}
      </div>
    )
  }

  const getInvitationActions = (gameData) => {
    const { isGroupRoot, gameStatus = gameData["status"], participants } = gameData
    const invitationActionObjects = []
    if (sponsorView && gameStatus === TABLE_GAME_STATUS_FILTER.ONGOING) {
      if (isGroupRoot) {
        // TODO
        // let uiDataObj = {
        //   iconName: "paper-plane",
        //   actionText: `Send Invitations`,
        //   clickHandler: () => handleUserActionInviteClick({ game: null, email: null }),
        //   enabled: true,
        // }
        // invitationActionObjects.push(uiDataObj)
      } else {
        // One invite per INVITABLE player — never AI participants (see helper).
        for (const aParticipant of invitableParticipants(participants)) {
          let uiDataObj = {
            iconName: "paper-plane",
            actionText: `Send Invitation: ${aParticipant.email}`,
            clickHandler: () => handleUserActionInviteClick({ game: gameData?.game_id, email: aParticipant.email }),
            enabled: true,
          }
          invitationActionObjects.push(uiDataObj)
        }
      }
    }

    return invitationActionObjects
  }

  const getScheduleAction = (gameStatus, gameId = null) => {
    const uiDataObj = { iconName: "", actionText: "", clickHandler: null, enabled: false }
    if (!sponsorView) {
      // No schedule action for players
    } else if (orgData?.archived) {
      // No schedule actions for archived orgs
    } else if (gameStatus === TABLE_GAME_STATUS_FILTER.ONGOING) {
      uiDataObj.iconName = "rotate-right"
      uiDataObj.actionText = TABLE_GAME_SCHEDULE_ACTION.RESTART
      uiDataObj.clickHandler = () => navigate(restartGameURL(gameId))
      uiDataObj.enabled = true
    } else if (gameStatus === TABLE_GAME_STATUS_FILTER.SCHEDULED) {
      uiDataObj.iconName = "pen-to-square"
      uiDataObj.actionText = TABLE_GAME_SCHEDULE_ACTION.EDIT
      uiDataObj.clickHandler = () => navigate(editGameURL(gameId))
      uiDataObj.enabled = true
    } else if (gameStatus === TABLE_GAME_STATUS_FILTER.FINISHED) {
      uiDataObj.iconName = "copy"
      uiDataObj.actionText = TABLE_GAME_SCHEDULE_ACTION.DUPLICATE
      uiDataObj.clickHandler = () => navigate(duplicateGameURL(gameId))
      uiDataObj.enabled = true
    }

    return uiDataObj
  }

  const renderUserAction = (uiDataObj, gameStatus) => (
    <div
      className={`game-user-action ${uiDataObj.enabled ? "clickable" : ""}`}
      data-game-status={`${gameStatus}`}
      style={{
        pointerEvents: uiDataObj.enabled ? undefined : "none",
        opacity: uiDataObj.enabled ? 1.0 : 0.4,
      }}
      onClick={uiDataObj.clickHandler}
    >
      <span className="icon-wrapper me-2">
        <i className={`fa-solid fa-${uiDataObj.iconName}`}></i>
      </span>
      <span className="status-text">{uiDataObj.actionText}</span>
    </div>
  )

  const getUserAction = ({ isGroupRoot, gameStatus, gameOrGroupId = null, gameReviewers, isCtf = false, isCtfLive = false, isGroupNode = false }) => {
    const uiDataObj = {
      iconName: "",
      actionText: "",
      clickHandler: null,
      enabled: false,
    }
    // CTF reports spec §2/§3/§6: a CTF group opens the staff event report and a finished CTF run
    // opens the CTF player report — for players too (their own report; the server gates the rest).
    // Before a CTF has finished there is no report yet: a live (host-paced) event offers its host
    // console instead; a self-paced CTF group shows nothing until it ends.
    const ctfFinished = gameStatus === TABLE_GAME_STATUS_FILTER.FINISHED
    if (isCtf && sponsorView && !ctfFinished && isCtfLive && !isGroupNode) {
      uiDataObj.iconName = "tower-broadcast"
      uiDataObj.actionText = "Host console"
      uiDataObj.clickHandler = () => navigate(ROUTE_PATHS.GAME_CTF_HOST.replace(":gameSessionId", encodeURIComponent(gameOrGroupId)))
      uiDataObj.enabled = true
      return renderUserAction(uiDataObj, gameStatus)
    }
    if (isCtf && isGroupRoot && sponsorView && !ctfFinished) return renderUserAction(uiDataObj, gameStatus)
    if (isCtf && isGroupRoot && sponsorView) {
      uiDataObj.iconName = "chart-area"
      uiDataObj.actionText = TABLE_GAME_USER_ACTION.VIEW_REPORT
      uiDataObj.clickHandler = () => navigate(ctfEventReportURL(gameOrGroupId))
      uiDataObj.enabled = true
      return renderUserAction(uiDataObj, gameStatus)
    }
    if (isCtf && !isGroupRoot && gameStatus === TABLE_GAME_STATUS_FILTER.FINISHED) {
      uiDataObj.iconName = "chart-area"
      uiDataObj.actionText = TABLE_GAME_USER_ACTION.VIEW_REPORT
      uiDataObj.clickHandler = () => navigate(ctfReportURL(gameOrGroupId))
      uiDataObj.enabled = true
      return renderUserAction(uiDataObj, gameStatus)
    }
    if (isGroupRoot) {
      uiDataObj.iconName = "chart-area"
      uiDataObj.actionText = TABLE_GAME_USER_ACTION.VIEW_DASHBOARD
      // TODO: setup proper link to the group dashboard
      uiDataObj.clickHandler = () => navigate(groupDashboardURL(gameOrGroupId))
      uiDataObj.enabled = true
    } else if (gameStatus === TABLE_GAME_STATUS_FILTER.ONGOING) {
      uiDataObj.iconName = "chess-rook"
      uiDataObj.actionText = TABLE_GAME_USER_ACTION.VIEW_GAME
      uiDataObj.clickHandler = () => navigate(playboardURL(gameOrGroupId))
      uiDataObj.enabled = true
    } else if (gameStatus === TABLE_GAME_STATUS_FILTER.SCHEDULED) {
      uiDataObj.iconName = ""
      uiDataObj.actionText = ""
    } else if (gameStatus === TABLE_GAME_STATUS_FILTER.FINISHED) {
      if (sponsorView || listIncludesUserEmail({ user: authUser, list: gameReviewers })) {
        uiDataObj.iconName = "chart-area"
        uiDataObj.actionText = TABLE_GAME_USER_ACTION.VIEW_REPORT
        uiDataObj.clickHandler = () => navigate(reportURL(gameOrGroupId))
        uiDataObj.enabled = true
      } else {
        // Note: Players may only review completed games
        uiDataObj.iconName = "chess-rook"
        uiDataObj.actionText = TABLE_GAME_USER_ACTION.VIEW_GAME
        uiDataObj.clickHandler = () => navigate(playboardURL(gameOrGroupId))
        uiDataObj.enabled = true
      }
    }

    return renderUserAction(uiDataObj, gameStatus)
  }

  const getAdminActions = ({
    isGroupRoot,
    gameStatus,
    gameOrGroupId = null,
    org = null,
    archived,
    ended,
    isMultiPlayer = false,
    isGroupNode = false,
  }) => {
    const adminActionObjects = []

    const isOngoing = gameStatus === TABLE_GAME_STATUS_FILTER.ONGOING
    const isFinished = gameStatus === TABLE_GAME_STATUS_FILTER.FINISHED
    const isScheduled = gameStatus === TABLE_GAME_STATUS_FILTER.SCHEDULED

    // Add Users — participants (group) or a player (collaborative session).
    // Only for MULTI-PLAYER exercises (group, or a session with >1 participant);
    // solo sessions and single group-member rows don't offer it. Allowed while
    // scheduled/ongoing (not finished/archived).
    if (isMultiPlayer && (isOngoing || isScheduled) && !archived && !ended && (sponsorView || managerView)) {
      adminActionObjects.push({
        iconName: "user-plus",
        actionText: TABLE_GAME_ADMIN_ACTION.ADD_USERS,
        clickHandler: () => handleAddUsersClick({ game: gameOrGroupId, org, isGroupRoot }),
        enabled: true,
      })
    }

    // Mark Finished / Unmark Finished
    if (isOngoing || isFinished || isScheduled) {
      if (sponsorView || managerView) {
        const uiDataObj = {
          iconName: "",
          actionText: "",
          clickHandler: null,
          enabled: true,
        }
        if (ended === true) {
          uiDataObj.iconName = "ban"
          uiDataObj.actionText = TABLE_GAME_ADMIN_ACTION.UNMARK_GAME_FINISHED
          if (isGroupRoot) {
            uiDataObj.clickHandler = () => handleUnmarkGroupGameFinishedClick({ game: gameOrGroupId, org })
          } else {
            uiDataObj.clickHandler = () => handleUnmarkGameFinishedClick({ game: gameOrGroupId })
          }
          adminActionObjects.push(uiDataObj)
        } else if (isOngoing || isScheduled) {
          uiDataObj.iconName = "flag-checkered"
          uiDataObj.actionText = TABLE_GAME_ADMIN_ACTION.MARK_GAME_FINISHED
          if (isGroupRoot) {
            uiDataObj.clickHandler = () => handleMarkGroupGameFinishedClick({ game: gameOrGroupId, org })
          } else {
            uiDataObj.clickHandler = () => handleMarkGameFinishedClick({ game: gameOrGroupId })
          }
          adminActionObjects.push(uiDataObj)
        }
      }
    }

    // Archive / Unarchive — sponsors manage their own org's exercises (updateSession /
    // updateSessionGroup already permit sponsor/trainer server-side; only this UI gate
    // hid the action from them).
    if (isOngoing || isFinished || isScheduled) {
      if (managerView || sponsorView) {
        const uiDataObj = {
          iconName: "",
          actionText: "",
          clickHandler: null,
          enabled: false,
        }
        if (archived) {
          uiDataObj.iconName = "box-open"
          uiDataObj.actionText = TABLE_GAME_ADMIN_ACTION.UNARCHIVE_GAME
          if (isGroupRoot) {
            uiDataObj.clickHandler = () => handleUnarchiveGroupGameClick({ game: gameOrGroupId, org })
          } else {
            uiDataObj.clickHandler = () => handleUnarchiveGameClick({ game: gameOrGroupId })
          }
        } else {
          uiDataObj.iconName = "box-archive"
          uiDataObj.actionText = TABLE_GAME_ADMIN_ACTION.ARCHIVE_GAME
          if (isGroupRoot) {
            uiDataObj.clickHandler = () => handleArchiveGroupGameClick({ game: gameOrGroupId, org })
          } else {
            uiDataObj.clickHandler = () => handleArchiveGameClick({ game: gameOrGroupId })
          }
        }
        uiDataObj.enabled = true
        adminActionObjects.push(uiDataObj)
      }
    }

    // Delete (permanent) — sponsors manage their own org's exercises (the server binds
    // them to it); managers may delete anywhere. Confirmation lives in the handler.
    // A single group-member row can't be deleted on its own (the server refuses and
    // points at the group) — the action belongs on the group root only.
    if ((managerView || sponsorView) && !isGroupNode && typeof handleDeleteGame === "function") {
      adminActionObjects.push({
        iconName: "trash-can",
        actionText: TABLE_GAME_ADMIN_ACTION.DELETE_GAME,
        clickHandler: () => handleDeleteGame({ game: gameOrGroupId, org, isGroupRoot }),
        enabled: true,
      })
    }

    return adminActionObjects
  }

  const getGameAction = (gameData) => {
    const {
      gameId = gameData?.game_id,
      orgId = gameData?.org_id,
      isGroupRoot,
      gameStatus = gameData["status"],
      isArchived = gameData?.archived,
      isEnded = gameData?.ended,
    } = gameData
    const actionList = []

    // Compare runs (KPI analytics only): finished exercises open the Compare runs tab. Players and
    // organizations without KPI analytics don't get it.
    if (kpiViewOfRow(gameData) !== KPI_VIEW.NONE && !gameData?.isGroupNode && gameStatus === TABLE_GAME_STATUS_FILTER.FINISHED) {
      // KPI P2 §10: the Compare runs tab (a session's Report, or the group dashboard).
      actionList.push({
        key: "history_action",
        gameStatus,
        iconName: "chart-line",
        actionText: TABLE_GAME_HISTORY_ACTION.COMPARE_RUNS,
        clickHandler: () =>
          navigate(isGroupRoot ? `/group-dashboard/${encodeURIComponent(gameId)}?tab=compare` : `/report/${encodeURIComponent(gameId)}?tab=compare`),
        enabled: true,
      })
    }
    // Live (host-paced) CTF: staff run the event from the host console; the presenter view goes on
    // the projector. Offered on the exercise row (a group root, or a solo live session).
    if (gameData?.ctfLive && staffView && !gameData?.isGroupNode && !isArchived) {
      const hostPath = ROUTE_PATHS.GAME_CTF_HOST.replace(":gameSessionId", encodeURIComponent(gameId))
      actionList.push({
        key: "ctf_host_action",
        gameStatus,
        iconName: "tower-broadcast",
        actionText: "Open host console",
        clickHandler: () => navigate(hostPath),
        enabled: true,
      })
      actionList.push({
        key: "ctf_present_action",
        gameStatus,
        iconName: "display",
        actionText: "Open presenter view",
        clickHandler: () => window.open(`${process.env.PUBLIC_URL ?? ""}${hostPath}?view=present`, "_blank", "noopener"),
        enabled: true,
      })
    }
    // Dynamic-question CTF: the facilitator pack for this exercise's generated set, once it's ready.
    const rowDynamicQs = dynamicQuestionsOf(gameData)
    if (handleDownloadCtfPack && staffView && rowDynamicQs?.status === "ready" && !gameData?.isGroupNode && !isArchived) {
      actionList.push({
        key: "ctf_pack_action",
        gameStatus,
        iconName: "download",
        actionText: "Download CTF facilitator pack",
        clickHandler: () => handleDownloadCtfPack({ name: gameId }),
        enabled: true,
      })
    }

    // Multi-player = a group (multiple participants across member sessions) or a
    // standalone collaborative session with >1 participant. Solo sessions and a
    // single group-member row are single-player and don't get "Add Users".
    const participantCount = Array.isArray(gameData?.participants) ? gameData.participants.length : 0
    const isMultiPlayer = !!isGroupRoot || (!gameData?.isGroupNode && participantCount > 1)

    const invitationActions = getInvitationActions(gameData)
    const sceduleAction = getScheduleAction(gameStatus, gameId)
    const adminActions = getAdminActions({
      isGroupRoot,
      gameStatus,
      gameOrGroupId: gameId,
      org: orgId,
      archived: isArchived,
      ended: isEnded,
      isMultiPlayer,
      isGroupNode: !!gameData?.isGroupNode,
    })

    invitationActions.forEach((invitationAction, idx) => {
      if (invitationAction.enabled) {
        actionList.push({
          key: `invitation_action_${idx}`, // should be unique
          gameStatus,
          ...invitationAction,
        })
      }
    })

    if (!isGroupRoot && sceduleAction.enabled) {
      actionList.push({
        key: "schedule_action", // should be unique
        gameStatus,
        ...sceduleAction,
      })
    }

    adminActions.forEach((adminAction, idx) => {
      if (adminAction.enabled) {
        actionList.push({
          key: `admin_action_${idx}`, // should be unique
          gameStatus,
          ...adminAction,
        })
      }
    })
    return actionList && actionList.length ? <GameAction gameId={gameId} actionList={actionList} /> : null
  }

  const getTableCellData = useCallback(
    (keyName, gameData) => {
      if (keyName === "group_key") {
        return getGroupKeySymbol(keyName, gameData)
      } else if (keyName === "game_scenario") {
        return getScenarioName(keyName, gameData)
      } else if (keyName === "game_name") {
        return getGameName(keyName, gameData)
      } else if (keyName === "org_name") {
        return getOrganizationName(keyName, gameData)
      } else if (keyName === "game_schedule") {
        return getGameSchedule(keyName, gameData)
      } else if (keyName === "people") {
        // One column for everyone on the exercise: players first, then observers behind an eye icon.
        const observerCount = Array.isArray(gameData.spectators) ? gameData.spectators.length : 0
        const observerLabel = `${observerCount} observer${observerCount === 1 ? "" : "s"}`
        return (
          <span className="d-inline-flex align-items-center gap-2" data-testid="people-cell">
            <UserProfiles keyName="participants" gameData={gameData} userData={userData} />
            {observerCount > 0 && (
              <Tooltip title={`${observerLabel} (view-only)`}>
                <span className="d-inline-flex align-items-center gap-1" aria-label={observerLabel}>
                  <VisibilityOutlined sx={{ fontSize: 16, color: "var(--ct-ink-3)" }} aria-hidden="true" />
                  <UserProfiles keyName="spectators" gameData={gameData} userData={userData} />
                </span>
              </Tooltip>
            )}
          </span>
        )
      } else if (keyName === "status") {
        return getGameStatus(gameData[keyName], gameData)
      } else if (keyName === "age") {
        return getGameAge(gameData)
      } else if (keyName === "kpi_score") {
        if (gameData.isGroupNode) return ""
        // CTF points and rank aren't KPI data: shown whatever the row's org KPI setting.
        if (gameData.exerciseTypeResolved !== "ctf" && kpiViewOfRow(gameData) !== KPI_VIEW.DATA)
          return (
            <span className="text-muted" title={kpiMissingReason(gameData)}>
              {"—"}
            </span>
          )
        // CTF reports spec §5/§6: CTF rows show points + rank, never a readiness %; Training is unscored.
        if (gameData.exerciseTypeResolved === "ctf") {
          const sum = gameData.ctfSummary
          if (gameData.isGroupRoot && gameData.ctfGroupTop) {
            const top = gameData.ctfGroupTop
            return (
              <span className="text-nowrap">{`Top ${top.score.toLocaleString("en-US")} pts · ${top.players} player${top.players === 1 ? "" : "s"}`}</span>
            )
          }
          return sum && typeof sum.score === "number" ? (
            <span className="text-nowrap">{`${sum.score.toLocaleString("en-US")} pts${sum.rank && sum.of ? ` · #${sum.rank}/${sum.of}` : ""}`}</span>
          ) : (
            "—"
          )
        }
        if (gameData.exerciseTypeResolved === "training" || gameData.guidanceMode === "guided")
          return (
            <span className="text-muted" title="Training exercises aren't scored">
              {"—"}
            </span>
          )
        return typeof gameData.score === "number" ? `${gameData.score}%` : "—"
      } else if (keyName === "user_action") {
        return getUserAction({
          isGroupRoot: gameData.isGroupRoot,
          isGroupNode: gameData.isGroupNode,
          gameStatus: gameData["status"],
          gameReviewers: gameData?.reviewers ?? [],
          gameOrGroupId: gameData?.game_id,
          isCtf: gameData?.exerciseTypeResolved === "ctf",
          isCtfLive: !!gameData?.ctfLive && gameData?.archived !== true,
        })
      } else if (keyName === "action") {
        return getGameAction(gameData)
      } else {
        return ""
      }
    },
    // typeFilter: the name cell hides the type chip while the list is filtered to one type.
    // kpiOrgEnabled / the view flags: the Score cell and Compare runs follow the (late-loading) KPI decision.
    [userData, authUser, typeFilter, kpiOrgEnabled, managerView, sponsorView]
  )

  const getGroupContent = useCallback(
    ({ groupId, isManagerView, isSponsorView }) => {
      const id = getLastIdAfterComma(groupId)
      return groupNodes[id].map((contentRow, contentRowIndex) => (
        <Fragment key={`${id}-table-data-row-${contentRowIndex}`}>
          <tr
            className={`table-row align-middle ${contentRow.isGroupRoot ? "group-root" : contentRow.isGroupNode ? "group-node" : ""} ${
              contentRow.archived && isManagerView ? "archived" : ""
            }`}
          >
            {withKpiColumns(tableDataRef.current.tableColumnHeaders).map((headerObj, headerIndex) => (
              <td key={`${id}-table-data-row-${contentRowIndex}-column-${headerIndex}`} className={`table-cell`}>
                {getTableCellData(headerObj.headerKey, contentRow)}
              </td>
            ))}
          </tr>
          <tr
            className={`row-spacer ${
              contentRow.isGroupRoot || contentRow.isGroupNode ? (isGroupOpened(contentRow.groupId) ? "group-spacer-opened" : "group-spacer") : ""
            }`}
          >
            <td className="table-cell" colSpan={withKpiColumns(tableDataRef.current.tableColumnHeaders).length}></td>
          </tr>
          {contentRow.groupId && contentRow.groupId !== id && isGroupOpened(contentRow.groupId) ? getGroupContent(contentRow.groupId) : null}
        </Fragment>
      ))
    },
    [groupNodes, getTableCellData, isGroupOpened]
  )

  return (
    <section className="section-gamelist px-3 h-100">
      <div className="page-header-wrapper ct-page-header" id="p_header">
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <h1 className="ct-page-title">Exercises</h1>
          <HelpTip />
        </div>
        {sponsorView ? (
          <div className="action-wrapper m-0">
            {orgData?.archived ? (
              <></>
            ) : (
              <button type="button" className="ct-new-exercise-btn" onClick={() => navigate(ROUTE_PATHS.GAME_ADD_NEW)}>
                <span className="ct-neb-plus">+</span>
                New Exercise
              </button>
            )}
          </div>
        ) : (
          <></>
        )}
      </div>

      <div className="page-content-wrapper position-relative">
        <div className="table-header-actions" id="t_action">
          <div className="search-box-wrapper">
            <div className="search-wrapper">
              <input
                type="search"
                name="user-commands"
                id="user-commands"
                onKeyUp={handleKeyEventsOnSearch}
                onChange={(event) => setSearchText(event.target.value)}
                value={searchText}
                placeholder="Search exercises"
              />
              <span className={`${searchText ? "clickable" : "invisible"}`} onClick={searchText ? handleClearSearchInputClick : undefined}>
                <i className="fa-solid fa-xmark"></i>
              </span>
              <img src="images/search_grey_icon.png" alt="Search Icon" />
            </div>
          </div>
          <div className="filter-box-wrapper desktop-filters">
            {filters.map((filter, filterIndex) => (
              <CustomFilter
                filterData={filter}
                currentValues={filterData}
                selectionChange={handleSelectionChange}
                key={`filterdata-${filterIndex}`}
                isAdmin={managerView}
              />
            ))}
          </div>
          <div className="mobile-filter-icon-wrapper">
            <UiCtButton onClick={handleMobileFilterOpen} startIcon={<FilterList />} variant="outlined">
              Apply filters
            </UiCtButton>
          </div>
        </div>

        {/* Mobile Filter Drawer */}
        <MobileFilterDrawer
          isOpen={mobileFilterDrawerOpen}
          onClose={handleMobileFilterClose}
          filters={filters}
          currentFilterValues={filterData}
          managerView={managerView}
          onFilterChange={handleSelectionChange}
        />

        {!filterSearchProgress && tableDataRef.current.tableColumnHeaders.length && (
          <div className="table-content-wrapper">
            {onTypeFilterChange ? (
              <div className="ct-exercises-type-filter">
                <span className="muted">{"Type"}</span>
                <ExerciseTypeFilter
                  value={typeFilter}
                  counts={typeCounts(tableDataRef.current.groupedData.filter((r) => matchesSetupFilter({ row: r, setup: setupFilter })))}
                  onChange={onTypeFilterChange}
                  includeCtf={ctfEnabled(orgData) || typeFilter === "ctf" || typeCounts(tableDataRef.current.groupedData).ctf > 0}
                />
              </div>
            ) : null}
            {filterChips}
            {visibleRows(tableDataRef.current.groupedData).length ? (
              /* Height comes from the flex column in Home.scss — no DOM-measured
                 calc (it guessed 118px before the header mounted and clipped the
                 last rows + pagination bar) */
              <div className="table-responsive">
                <table className="table game-list-table">
                  <thead>
                    <tr>
                      {withKpiColumns(tableDataRef.current.tableColumnHeaders).map((headerObj, headerIndex) => (
                        <th
                          scope="col"
                          key={`header-label-${headerIndex}`}
                          className={`${headerObj.headerKey}`}
                          style={{
                            minWidth: `${headerObj.minWidth || 90}px`,
                          }}
                        >
                          {headerObj.headerTooltip ? (
                            <Tooltip title={headerObj.headerTooltip} arrow placement="top">
                              <span>{headerObj.headerLabel}</span>
                            </Tooltip>
                          ) : (
                            headerObj.headerLabel
                          )}
                          {sortableColumnKeys.includes(headerObj.headerKey) ? (
                            <span
                              className="icon-wrapper sort-icon ps-2"
                              data-sort_mode={sortMode[headerObj.headerKey]}
                              onClick={() => handleTableSortClick(headerObj.headerKey)}
                            >
                              <i className="fa-solid fa-arrow-up"></i>
                              <i className="fa-solid fa-arrow-down"></i>
                            </span>
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Client-side pagination over top-level rows (group children render
                      // within their parent row, so groups never split across pages)
                      const total = visibleRows(tableDataRef.current.groupedData).length
                      const totalPages = Math.max(1, Math.ceil(total / listPageSize))
                      const page = Math.min(listPage, totalPages)
                      return visibleRows(tableDataRef.current.groupedData)
                        .slice((page - 1) * listPageSize, page * listPageSize)
                        .map((gameData, sliceIndex) => {
                          const dataIndex = (page - 1) * listPageSize + sliceIndex
                          return (
                            <GameTableRow
                              key={`game-row-${dataIndex}`}
                              gameData={gameData}
                              dataIndex={dataIndex}
                              tableColumnHeaders={withKpiColumns(tableDataRef.current.tableColumnHeaders)}
                              managerView={managerView}
                              getTableCellData={getTableCellData}
                              isGroupOpened={isGroupOpened}
                              getGroupContent={getGroupContent}
                              sponsorView={sponsorView}
                            />
                          )
                        })
                    })()}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyGame sponsorView={sponsorView} isFilterEmpty={true} />
            )}
            {visibleRows(tableDataRef.current.groupedData).length > 0 && (
              <ListPagination
                total={visibleRows(tableDataRef.current.groupedData).length}
                page={listPage}
                pageSize={listPageSize}
                onPageChange={setListPage}
                onPageSizeChange={(n) => {
                  setListPageSize(n)
                  setListPage(1)
                }}
                label="Exercise pages"
              />
            )}
          </div>
        )}
        {/* Table Filter Loader */}
        {filterSearchProgress && <Loader />}
      </div>
    </section>
  )
}

export default GameList
